import { useMemo, useRef, useState } from "react";
import { Button } from "@/project/karuna/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/project/karuna/components/ui/dialog";
import { Loader2 } from "lucide-react";
import { createForm } from "@formily/core";
import SchemaFormAutoSubmit from "@/components/Schema/SchemaFormAutoSubmit";

const SCHEMA_NAME = "Activity Management";

export type EditActivityDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  recordId: string;
  onUpdated?: () => void;
};

export function EditActivityDialog({
  open,
  onOpenChange,
  recordId,
  onUpdated,
}: EditActivityDialogProps) {
  const [formResetKey, setFormResetKey] = useState(0);
  const [loading, setLoading] = useState(false);
  const formRef = useRef<{ handleSave: () => Promise<void> }>(null);
  // New form per activity — a single shared form kept stale values when switching records.
  // eslint-disable-next-line react-hooks/exhaustive-deps -- recordId intentionally recreates the form when switching activities
  const form = useMemo(() => createForm({}), [recordId]);

  const handleSubmit = async () => {
    if (!formRef.current) {
      return;
    }
    setLoading(true);
    try {
      await formRef.current.handleSave();
      onUpdated?.();
      onOpenChange(false);
    } catch (_error: unknown) {
      // Error handled by backend / form
    } finally {
      setLoading(false);
    }
  };

  if (!recordId) {
    return null;
  }

  return (
    <Dialog
      open={open}
      onOpenChange={next => {
        onOpenChange(next);
        if (next) {
          setFormResetKey(k => k + 1);
        }
      }}
    >
      <DialogContent className="flex max-h-[90vh] max-w-4xl flex-col gap-0 overflow-visible p-0 sm:rounded-lg">
        <div className="min-h-0 flex-1 overflow-y-auto px-6 pt-6">
          <DialogHeader>
            <DialogTitle className="text-2xl">Edit Activity</DialogTitle>
            <DialogDescription>Update activity details</DialogDescription>
          </DialogHeader>

          <div className="space-y-6 pb-4">
            <SchemaFormAutoSubmit
              key={`${recordId}-${formResetKey}`}
              ref={formRef}
              form={form}
              schemaName={SCHEMA_NAME}
              recordId={recordId}
            />
          </div>
        </div>

        <DialogFooter className="border-t border-border bg-card px-6 py-4">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleSubmit}
            disabled={loading}
            className="bg-gradient-to-r from-primary to-primary/80 text-white hover:from-primary/90 hover:to-primary/90"
          >
            {loading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Updating...
              </>
            ) : (
              "Update Activity"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
